import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import api, { formatMoney, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function GuiaDespacho() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    api.get(`/orders/${id}`).then(({ data }) => setOrder(data));
  }, [id]);

  if (!order) return <div className="p-6 text-sm text-zinc-500">Cargando...</div>;

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="guia-despacho-page">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
          .print-page { padding: 0 !important; }
        }
      `}</style>

      <div className="no-print border-b border-zinc-200 px-4 py-3 flex items-center justify-between sticky top-0 bg-white z-10">
        <Link to={`/pedidos/${id}`} className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900">
          <ArrowLeft className="w-4 h-4" /> Volver al pedido
        </Link>
        <Button onClick={() => window.print()} className="bg-[#002FA7] hover:bg-[#00227A] gap-2" data-testid="print-guia-btn">
          <Printer className="w-4 h-4" /> Imprimir
        </Button>
      </div>

      <div className="print-page max-w-3xl mx-auto p-10 space-y-8">
        <header className="flex items-start justify-between border-b-2 border-zinc-900 pb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Guía de despacho</div>
            <div className="font-manrope font-black tracking-tight text-4xl mt-1">{order.numero}</div>
            <div className="text-xs text-zinc-500 mt-1">Emitida: {formatDate(order.fecha)}</div>
          </div>
          <div className="text-right">
            <QRCodeSVG value={order.numero} size={110} level="H" />
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Escanear</div>
          </div>
        </header>

        <section className="grid grid-cols-2 gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Cliente</div>
            <div className="font-semibold text-lg">{order.cliente_nombre}</div>
            {order.cliente_telefono && <div className="text-sm">{order.cliente_telefono}{order.cliente_telefono2 ? ` · alt ${order.cliente_telefono2}` : ""}</div>}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Envío</div>
            <div className="font-semibold">{order.envio_nombre || "—"}</div>
            <div className="text-xs text-zinc-600 capitalize">{order.envio_tipo}{order.envio_zona ? ` · ${order.envio_zona}` : ""}</div>
          </div>
          <div className="col-span-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Dirección de entrega</div>
            <div className="text-sm">{order.direccion}</div>
            {order.fecha_entrega && <div className="text-xs text-zinc-500 mt-1">Entrega prevista: {formatDate(order.fecha_entrega)}</div>}
          </div>
        </section>

        <section>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Detalle de productos</div>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-zinc-900 text-left">
                <th className="py-2 pr-2">Producto</th>
                <th className="py-2 pr-2">Variante</th>
                <th className="py-2 pr-2 text-right">Cant.</th>
                <th className="py-2 pr-2 text-right">Precio</th>
                <th className="py-2 pr-2 text-right">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((it, i) => (
                <tr key={i} className="border-b border-zinc-200">
                  <td className="py-2 pr-2 font-medium">{it.product_name}</td>
                  <td className="py-2 pr-2 text-zinc-600 text-xs">{[it.color, it.tamano, it.estilo].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{it.cantidad}</td>
                  <td className="py-2 pr-2 text-right tabular-nums">{formatMoney(it.precio_unit)}</td>
                  <td className="py-2 pr-2 text-right tabular-nums font-semibold">{formatMoney(it.subtotal)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} className="pt-3 text-right text-zinc-500">Subtotal</td>
                <td className="pt-3 text-right tabular-nums">{formatMoney(order.subtotal)}</td>
              </tr>
              <tr>
                <td colSpan={4} className="text-right text-zinc-500">Envío</td>
                <td className="text-right tabular-nums">{formatMoney(order.envio_precio)}</td>
              </tr>
              <tr>
                <td colSpan={4} className="pt-2 text-right font-manrope font-black text-lg">TOTAL</td>
                <td className="pt-2 text-right tabular-nums font-manrope font-black text-lg">{formatMoney(order.total)}</td>
              </tr>
              {order.anticipo ? (
                <>
                  <tr>
                    <td colSpan={4} className="text-right text-zinc-500">
                      Anticipo
                      {order.anticipo_metodo ? (
                        <span className="text-[10px] uppercase tracking-widest ml-1">
                          ({order.anticipo_metodo}{order.anticipo_autorizacion ? ` · ${order.anticipo_autorizacion}` : ""})
                        </span>
                      ) : null}
                    </td>
                    <td className="text-right tabular-nums">-{formatMoney(order.anticipo)}</td>
                  </tr>
                  <tr>
                    <td colSpan={4} className="pt-2 text-right font-manrope font-black text-lg text-emerald-700">A COBRAR</td>
                    <td className="pt-2 text-right tabular-nums font-manrope font-black text-lg text-emerald-700">{formatMoney(order.a_cobrar != null ? order.a_cobrar : (order.total - order.anticipo))}</td>
                  </tr>
                </>
              ) : null}
            </tfoot>
          </table>
        </section>

        {order.notas && (
          <section>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Notas</div>
            <div className="text-sm border border-zinc-200 rounded-md p-3 mt-1">{order.notas}</div>
          </section>
        )}

        <section className="grid grid-cols-2 gap-8 pt-8">
          <div className="border-t border-zinc-900 pt-2 text-center text-xs text-zinc-600">
            Firma del cliente
          </div>
          <div className="border-t border-zinc-900 pt-2 text-center text-xs text-zinc-600">
            Firma del piloto ({order.piloto_nombre || "sin asignar"})
          </div>
        </section>
      </div>
    </div>
  );
}
